import React, { useState } from 'react';
import styled from 'styled-components';
import { Order } from '../../../types/order/Order';
import { ConnectedStore } from '../../../types/ConnectedStore';
import ModalContainer from '../../atoms/Modal/ModalContainer';
import HeaderTitle from '../../atoms/Modal/HeaderTitle';
import FooterButtonContainer from '../../atoms/Modal/FooterButtonContainer';
import Button from '../../atoms/Button/Button';
import CheckBox from '../../atoms/Checkbox/Checkbox';
import orderExportColumns from '../../../constants/orderExportColumns';
import { ExportColumn, generateOrdersCsv, downloadCsv } from '../../../services/helpers/csvExport';

interface ExportOrdersModalProps {
    orders: Order[];
    stores: ConnectedStore[];
    visibleColumns?: string[];
    hide: () => void;
}

const getInitialColumns = (visibleColumns: string[]): Set<string> => {
    if (!visibleColumns.length)
        return new Set(orderExportColumns.map(c => c.key));

    return new Set(
        orderExportColumns
            .filter(c => c.tableHeader && visibleColumns.includes(c.tableHeader))
            .map(c => c.key)
    );
};

const ExportOrdersModal: React.FC<ExportOrdersModalProps> = ({ orders, stores, visibleColumns = [], hide }) => {
    const [selectedColumnKeys, setSelectedColumnKeys] = useState<Set<string>>(
        () => getInitialColumns(visibleColumns)
    );

    const allSelected = selectedColumnKeys.size === orderExportColumns.length;

    const toggleColumn = (key: string) => {
        setSelectedColumnKeys(prev => {
            const next = new Set(prev);
            if (next.has(key))
                next.delete(key);
            else
                next.add(key);
            return next;
        });
    };

    const toggleAll = () => {
        if (allSelected)
            setSelectedColumnKeys(new Set());
        else
            setSelectedColumnKeys(new Set(orderExportColumns.map(c => c.key)));
    };

    const handleExport = () => {
        const columns: ExportColumn[] = orderExportColumns.filter(c =>
            selectedColumnKeys.has(c.key)
        );
        const csv = generateOrdersCsv(orders, columns, stores);
        const today = new Date().toISOString().slice(0, 10);
        downloadCsv(csv, `orders-export-${today}.csv`);
        hide();
    };

    return (
        <>
            <ModalContainer position="header">
                <HeaderTitle>Export {orders.length} order{orders.length !== 1 ? 's' : ''} as CSV</HeaderTitle>
            </ModalContainer>

            <ModalContainer position="content">
                <SelectAllRow>
                    <CheckBox
                        dataTestId="export-select-all"
                        checked={allSelected}
                        onChange={toggleAll}
                    >
                        Select all
                    </CheckBox>
                </SelectAllRow>

                <ColumnGrid>
                    {orderExportColumns.map(col => (
                        <CheckBox
                            key={col.key}
                            dataTestId={`export-col-${col.key}`}
                            checked={selectedColumnKeys.has(col.key)}
                            onChange={() => toggleColumn(col.key)}
                        >
                            {col.label}
                        </CheckBox>
                    ))}
                </ColumnGrid>
            </ModalContainer>

            <ModalContainer position="footer">
                <FooterButtonContainer>
                    <Button data-testid="export-cancel-button" onClick={hide}>
                        Cancel
                    </Button>
                    <Button
                        data-testid="export-confirm-button"
                        primary
                        onClick={handleExport}
                        disabled={selectedColumnKeys.size === 0}
                    >
                        Export CSV
                    </Button>
                </FooterButtonContainer>
            </ModalContainer>
        </>
    );
};

const ColumnGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.2rem 1.5rem;
`;

const SelectAllRow = styled.div`
    padding-bottom: 0.4rem;
    margin-bottom: 0.4rem;
    border-bottom: 0.1rem solid ${props => props.theme.colors.border};
`;

export default ExportOrdersModal;
